import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useMutation, useQuery } from 'data-fetching-client';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import emptyFunction from 'react-utils/emptyFunction';
import getIn from 'immutable-less/transmute/getIn';
import { NetworkStatus } from '@apollo/client';
import H2 from 'UIComponents/elements/headings/H2';
import UIButton from 'UIComponents/button/UIButton';
import UIDialogBody from 'UIComponents/dialog/UIDialogBody';
import UIDialogCloseButton from 'UIComponents/dialog/UIDialogCloseButton';
import UIDialogFooter from 'UIComponents/dialog/UIDialogFooter';
import UIDialogHeader from 'UIComponents/dialog/UIDialogHeader';
import UIForm from 'UIComponents/form/UIForm';
import UIFormControl from 'UIComponents/form/UIFormControl';
import UIModal from 'UIComponents/dialog/UIModal';
import UITextInput from 'UIComponents/input/UITextInput';
import UILoadingSpinner from 'UIComponents/loading/UILoadingSpinner';
import { getPropertyValue } from 'framework-listing-lib/utils/crmObject';
import BusinessUnitSelect from 'ui-business-units-lib/components/BusinessUnitSelect';
import WithBusinessUnitsDataFetching from 'ui-business-units-lib/utils/WithBusinessUnitsDataFetching';
import { BusinessUnitsRebrandMessage } from 'ui-business-units-lib/components/BusinessUnitsRebrandMessage';
import { getFlowEditRoute } from 'automation-ui-routing/utils';
import { reportError } from 'automation-ui-core/errors/utils/reportError';
import { useHasScope } from 'automation-ui-core/hooks/authHooks';
import TrackableButton from 'automation-ui-usage-tracking/components/TrackableButton';
import Trackables from 'automation-ui-usage-tracking/Trackables';
import * as SourceApps from 'automation-ui-source-apps/constants/SourceApps';
import Scopes from 'automation-ui-management/constants/Scopes';
import { useHasUnsupportedReenrollment } from 'automation-ui-management/hooks/supportedReenrollment';
import { CLONE_FLOW_FIELD_NAME, CLONE_FLOW } from 'automation-ui-management/queries/flowQueries';
import { CRM_OBJECT_SEARCH_FIELD_NAME, CRM_OBJECT_SEARCH_BY_FLOW_ID } from 'automation-ui-management/queries/crmObjectQueries';
import { AutomationPlatformFlowProperties } from 'automation-ui-management/constants/CrmWorkflowProperties';
import WorkflowFetchFailureAlert from 'automation-ui-management/components/modals/alerts/WorkflowFetchFailureAlert';
import CloneWorkflowRequestFailureAlert from 'automation-ui-management/components/modals/CloneWorkflowModal/CloneWorkflowRequestFailureAlert';
import CloneWorkflowReferencesSection from 'automation-ui-management/components/modals/CloneWorkflowModal/CloneWorkflowReferencesSection';
import CloneWorkflowLengthyRequestAlert from 'automation-ui-management/components/modals/CloneWorkflowModal/CloneWorkflowLengthyRequestAlert';
import CloneWorkflowExternallyCreatedAlert from 'automation-ui-management/components/modals/CloneWorkflowModal/CloneWorkflowExternallyCreatedAlert';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const BusinessUnitSelectorWithDataFetching = WithBusinessUnitsDataFetching(BusinessUnitSelect);
const CloneWorkflowModal = ({
  FailureAlert = CloneWorkflowRequestFailureAlert,
  flowId,
  onClose,
  onFailure = emptyFunction,
  onSuccess = emptyFunction
}) => {
  const history = useHistory();
  const [clonedWorkflowName, setClonedWorkflowName] = useState('');
  const [businessUnitId, setBusinessUnitId] = useState();
  const [isLengthyCloneRequest, setIsLengthyCloneRequest] = useState(false);
  const hasBusinessUnitScope = useHasScope(Scopes.BUSINESS_UNITS_ACCESS);
  const {
    loading: unsupportedReenrollmentLoading,
    hasUnsupportedReenrollmentTriggers
  } = useHasUnsupportedReenrollment({
    flowId
  });
  const {
    loading,
    data,
    networkStatus,
    refetch
  } = useQuery(CRM_OBJECT_SEARCH_BY_FLOW_ID, {
    variables: {
      flowId: String(flowId)
    },
    onCompleted({
      [CRM_OBJECT_SEARCH_FIELD_NAME]: crmFlowObject
    }) {
      const name = getPropertyValue(crmFlowObject, AutomationPlatformFlowProperties.NAME);
      const clonedName = I18n.text('automation-ui-management.CloneWorkflowModal.clonedName');
      setClonedWorkflowName(name.concat(clonedName));
      const buId = getPropertyValue(crmFlowObject, AutomationPlatformFlowProperties.BUSINESS_UNIT);
      if (buId) {
        setBusinessUnitId(parseInt(buId, 10));
      }
    },
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true
  });
  const [cloneFlow, cloneFlowMutation] = useMutation(CLONE_FLOW);
  const crmObject = getIn([CRM_OBJECT_SEARCH_FIELD_NAME], data);
  const version = crmObject ? Number(getPropertyValue(crmObject, AutomationPlatformFlowProperties.VERSION)) : null;
  const handleSave = () => {
    setIsLengthyCloneRequest(false);
    const timer = setTimeout(() => {
      setIsLengthyCloneRequest(true);
    }, 14000);
    const frameworkFolderId = crmObject ? getPropertyValue(crmObject, AutomationPlatformFlowProperties.FOLDER_ID, undefined) : null;
    cloneFlow({
      variables: {
        flowId,
        // dev note (SE) - save button is disabled if version is missing
        version: version,
        sourceApp: SourceApps.WORKFLOWS_APP,
        name: clonedWorkflowName,
        frameworkFolderId: frameworkFolderId || undefined,
        businessUnitId
      }
    }).then(({
      data: clonedFlowData
    }) => {
      const clonedFlow = clonedFlowData[CLONE_FLOW_FIELD_NAME];
      onSuccess({
        flow: clonedFlow
      });
      const {
        flowId: clonedFlowId
      } = clonedFlow;
      const description = getPropertyValue(crmObject, AutomationPlatformFlowProperties.DESCRIPTION);
      const queryParams = {
        hasDescription: Boolean(description) || undefined
      };
      history.push(getFlowEditRoute(clonedFlowId, queryParams));
    }).catch(clonedFlowError => {
      reportError({
        error: clonedFlowError
      });
      onFailure();
    }).finally(() => clearTimeout(timer));
  };
  const handleNameChange = e => {
    setClonedWorkflowName(e.target.value);
  };
  const renderModalContent = () => {
    let bodyContent;
    const isLoading = loading || networkStatus === NetworkStatus.refetch;
    if (isLoading) {
      bodyContent = /*#__PURE__*/_jsx(UILoadingSpinner, {
        layout: "centered"
      });
    } else if (crmObject) {
      const sourceApp = getPropertyValue(crmObject, AutomationPlatformFlowProperties.CREATED_SOURCE_APP);
      const isExternallyCreated = sourceApp !== SourceApps.WORKFLOWS_APP;
      bodyContent = /*#__PURE__*/_jsxs(_Fragment, {
        children: [cloneFlowMutation.error && /*#__PURE__*/_jsx(FailureAlert, {}), !unsupportedReenrollmentLoading && isExternallyCreated && hasUnsupportedReenrollmentTriggers && /*#__PURE__*/_jsx(CloneWorkflowExternallyCreatedAlert, {}), /*#__PURE__*/_jsxs(UIForm, {
          children: [/*#__PURE__*/_jsx(CloneWorkflowReferencesSection, {
            flowId: flowId
          }), isLengthyCloneRequest && /*#__PURE__*/_jsx(CloneWorkflowLengthyRequestAlert, {}), /*#__PURE__*/_jsx(UIFormControl, {
            label: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "automation-ui-management.CloneWorkflowModal.enterName"
            }),
            children: /*#__PURE__*/_jsx(UITextInput, {
              "data-test-id": "clone-modal-text-field",
              disabled: cloneFlowMutation.loading,
              onChange: handleNameChange,
              value: clonedWorkflowName
            })
          }), hasBusinessUnitScope && /*#__PURE__*/_jsx(UIFormControl, {
            label: /*#__PURE__*/_jsx(BusinessUnitsRebrandMessage, {
              message: "automation-ui-management.CloneWorkflowModal.selectBusinessUnit",
              rebrandMessage: "automation-ui-management.CloneWorkflowModal.selectBusinessUnit_buv2"
            }),
            children: /*#__PURE__*/_jsx(BusinessUnitSelectorWithDataFetching, {
              buId: businessUnitId,
              onChange: buId => {
                setBusinessUnitId(buId);
              }
            })
          })]
        })]
      });
    } else {
      bodyContent =
      /*#__PURE__*/
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      _jsx(WorkflowFetchFailureAlert, {
        handleRefetch: () => refetch()
      });
    }
    return /*#__PURE__*/_jsx(UIDialogBody, {
      children: bodyContent
    });
  };
  return /*#__PURE__*/_jsxs(UIModal, {
    "data-test-id": "clone-modal-root",
    children: [/*#__PURE__*/_jsxs(UIDialogHeader, {
      children: [/*#__PURE__*/_jsx(UIDialogCloseButton, {
        disabled: cloneFlowMutation.loading,
        onClick: onClose
      }), /*#__PURE__*/_jsx(H2, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "automation-ui-management.CloneWorkflowModal.nameCloned"
        })
      })]
    }), renderModalContent(), /*#__PURE__*/_jsxs(UIDialogFooter, {
      children: [/*#__PURE__*/_jsx(TrackableButton, {
        onClick: handleSave,
        immediate: true,
        use: "primary",
        type: "submit",
        loading: cloneFlowMutation.loading,
        disabled: !version && version !== 0,
        trackableKey: Trackables.CLONED_WORKFLOW,
        "data-test-id": "clone-modal-submit-button",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "automation-ui-management.CloneWorkflowModal.submitButton"
        })
      }), /*#__PURE__*/_jsx(UIButton, {
        disabled: cloneFlowMutation.loading,
        use: "secondary",
        type: "submit",
        onClick: onClose,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "automation-ui-management.CloneWorkflowModal.cancelButton"
        })
      })]
    })]
  });
};
export default CloneWorkflowModal;